import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  Badge,
  Divider,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  AutoFixHigh as SparklesIcon,
  ArrowForward as ArrowForwardIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { api } from '../services/authService';
import ConnectionModal from './ConnectionModal';

const StepSidebar = ({ 
  step, 
  onUpdate, 
  saving, 
  onDelete, 
  steps = [], 
  connections = [], 
  onConnectTo = () => {}, 
  onEditConnection = () => {}, 
  onDeleteConnection = () => {}, 
  journey = null 
}) => {
  const [activeTab, setActiveTab] = useState('settings');
  const [error, setError] = useState(null);
  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);
  const [selectedTargetStep, setSelectedTargetStep] = useState('');
  const [triggerType, setTriggerType] = useState('immediate');
  const [delayDuration, setDelayDuration] = useState('');
  const [delayUnit, setDelayUnit] = useState('minutes');
  const [funnelStep, setFunnelStep] = useState('');
  const [eventType, setEventType] = useState('');
  const [conditionLabel, setConditionLabel] = useState('');
  const [conditionConfig, setConditionConfig] = useState({
    type: 'field_equals',
    field: '',
    value: ''
  });
  const [priority, setPriority] = useState(1);
  const [editingConnection, setEditingConnection] = useState(null);
  const [localStep, setLocalStep] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Initialize local state when step changes
  useEffect(() => {
    if (step) {
      setLocalStep(step);
      setHasChanges(false);
    }
  }, [step]);

  if (!step || !localStep) {
    return (
      <Box p={2} textAlign="center">
        <Typography variant="body2" color="text.primary">
          Select a step to configure it
        </Typography>
      </Box>
    );
  }

  const handleNameChange = (event) => {
    setLocalStep(prev => ({
      ...prev,
      name: event.target.value
    }));
    setHasChanges(true);
  };

  const handleConfigChange = (field, value) => {
    setLocalStep(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleUpdateStep = async () => {
    try {
      const res = await api.put(`/acs/journey-steps/${step.id}/`, localStep);
      onUpdate(step.id, res.data);
      setHasChanges(false);
      setError(null);
    } catch (err) {
      console.error('Error updating step:', err);
      setError('Failed to update step');
    }
  };

  const handleDeleteStep = () => {
    onDelete(step.id);
  };

  const handleEditConnection = (connection) => {
    setEditingConnection(connection);
    setSelectedTargetStep(connection.to_step);
    setTriggerType(connection.trigger_type);
    setDelayDuration(connection.delay_duration || '');
    setDelayUnit(connection.delay_unit || 'minutes');
    setFunnelStep(connection.funnel_step || '');
    setEventType(connection.event_type || '');
    setConditionLabel(connection.condition_label || '');
    setConditionConfig(connection.condition_config || { type: 'field_equals', field: '', value: '' });
    setPriority(connection.priority || 1);
    setIsConnectionModalOpen(true);
  };

  const handleCreateConnection = (config) => {
    if (editingConnection) {
      onEditConnection(editingConnection.id, {
        ...config,
        from_step: step.id,
        to_step: parseInt(selectedTargetStep)
      });
    } else {
      onConnectTo(step.id, parseInt(selectedTargetStep), config);
    }

    // Reset form
    setSelectedTargetStep('');
    setTriggerType('immediate');
    setDelayDuration('');
    setDelayUnit('minutes');
    setFunnelStep('');
    setEventType('');
    setConditionLabel('');
    setConditionConfig({ type: 'field_equals', field: '', value: '' });
    setPriority(1);
    setEditingConnection(null);
    setIsConnectionModalOpen(false);
  };

  const isValidConnection = () => {
    switch (triggerType) {
      case 'delay':
        return delayDuration > 0 && delayUnit;
      case 'funnel_change':
        return funnelStep;
      case 'event':
        return eventType;
      case 'condition':
        return conditionLabel && conditionConfig.field && conditionConfig.value;
      default:
        return true;
    }
  };

  const renderConfigFields = () => {
    switch (localStep.step_type) {
      case 'email':
      case 'sms':
      case 'voice':
      case 'chat':
        return (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Message Source
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={localStep.template ? 'template' : 'direct'}
                  onChange={(e) => {
                    if (e.target.value === 'template') {
                      handleConfigChange('template', null);
                    } else {
                      handleConfigChange('template', null);
                      handleConfigChange('content', '');
                    }
                  }}
                  disabled={saving}
                >
                  <MenuItem value="template">Use Template</MenuItem>
                  <MenuItem value="direct">Direct Message</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {localStep.template ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.primary" gutterBottom>
                  Template
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={localStep.template?.id || ''}
                    onChange={(e) => handleConfigChange('template', e.target.value)}
                    disabled={saving}
                  >
                    <MenuItem value="">Select a template</MenuItem>
                    {/* Template options will be populated from API */}
                  </Select>
                </FormControl>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" color="text.primary">Message Content</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SparklesIcon />}
                    disabled={saving}
                  >
                    Generate
                  </Button>
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  value={localStep.config?.content || ''}
                  onChange={(e) => handleConfigChange('content', e.target.value)}
                  disabled={saving}
                />
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Channel Settings
              </Typography>
              {localStep.step_type === 'email' && (
                <Box sx={{ space: 2 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      value={localStep.config?.channel_specific_settings?.subject || ''}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        subject: e.target.value
                      })}
                      label="Subject"
                      placeholder="Enter email subject"
                      InputLabelProps={{
                        shrink: !!localStep.config?.channel_specific_settings?.subject
                      }}
                      disabled={saving}
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      value={localStep.config?.channel_specific_settings?.from_email || ''}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        from_email: e.target.value
                      })}
                      label="From Email"
                      placeholder="sender@example.com"
                      InputLabelProps={{
                        shrink: !!localStep.config?.channel_specific_settings?.from_email
                      }}
                      disabled={saving}
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      value={localStep.config?.channel_specific_settings?.from_name || ''}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        from_name: e.target.value
                      })}
                      label="From Name"
                      placeholder="Your Company Name"
                      InputLabelProps={{
                        shrink: !!localStep.config?.channel_specific_settings?.from_name
                      }}
                      disabled={saving}
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      value={localStep.config?.channel_specific_settings?.reply_to || ''}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        reply_to: e.target.value
                      })}
                      label="Reply To"
                      placeholder="reply@example.com"
                      InputLabelProps={{
                        shrink: !!localStep.config?.channel_specific_settings?.reply_to
                      }}
                      disabled={saving}
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={localStep.config?.channel_specific_settings?.priority || 'normal'}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        priority: e.target.value
                      })}
                      label="Priority"
                      disabled={saving}
                    >
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localStep.config?.channel_specific_settings?.track_opens || false}
                        onChange={(e) => handleConfigChange('channel_specific_settings', {
                          ...localStep.config?.channel_specific_settings,
                          track_opens: e.target.checked
                        })}
                        disabled={saving}
                      />
                    }
                    label="Track Opens"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localStep.config?.channel_specific_settings?.track_clicks || false}
                        onChange={(e) => handleConfigChange('channel_specific_settings', {
                          ...localStep.config?.channel_specific_settings,
                          track_clicks: e.target.checked
                        })}
                        disabled={saving}
                      />
                    }
                    label="Track Clicks"
                  />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.primary" gutterBottom>
                      Attachments
                    </Typography>
                    {(localStep.config?.channel_specific_settings?.attachments || []).map((attachment, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          label="Name"
                          value={attachment.name}
                          onChange={(e) => {
                            const newAttachments = [...(localStep.config?.channel_specific_settings?.attachments || [])];
                            newAttachments[index] = { ...attachment, name: e.target.value };
                            handleConfigChange('channel_specific_settings', {
                              ...localStep.config?.channel_specific_settings,
                              attachments: newAttachments
                            });
                          }}
                          size="small"
                          InputLabelProps={{
                            shrink: !!attachment.name
                          }}
                          disabled={saving}
                        />
                        <TextField
                          label="URL"
                          value={attachment.url}
                          onChange={(e) => {
                            const newAttachments = [...(localStep.config?.channel_specific_settings?.attachments || [])];
                            newAttachments[index] = { ...attachment, url: e.target.value };
                            handleConfigChange('channel_specific_settings', {
                              ...localStep.config?.channel_specific_settings,
                              attachments: newAttachments
                            });
                          }}
                          size="small"
                          InputLabelProps={{
                            shrink: !!attachment.url
                          }}
                          disabled={saving}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newAttachments = [...(localStep.config?.channel_specific_settings?.attachments || [])];
                            newAttachments.splice(index, 1);
                            handleConfigChange('channel_specific_settings', {
                              ...localStep.config?.channel_specific_settings,
                              attachments: newAttachments
                            });
                          }}
                          disabled={saving}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newAttachments = [...(localStep.config?.channel_specific_settings?.attachments || []), { name: '', url: '' }];
                        handleConfigChange('channel_specific_settings', {
                          ...localStep.config?.channel_specific_settings,
                          attachments: newAttachments
                        });
                      }}
                      disabled={saving}
                    >
                      Add Attachment
                    </Button>
                  </Box>
                </Box>
              )}

              {localStep.step_type === 'sms' && (
                <Box sx={{ space: 2 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      value={localStep.config?.channel_specific_settings?.from_number || ''}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        from_number: e.target.value
                      })}
                      label="From Number"
                      placeholder="+1234567890"
                      InputLabelProps={{
                        shrink: !!localStep.config?.channel_specific_settings?.from_number
                      }}
                      disabled={saving}
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={localStep.config?.channel_specific_settings?.priority || 'normal'}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        priority: e.target.value
                      })}
                      label="Priority"
                      disabled={saving}
                    >
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localStep.config?.channel_specific_settings?.track_delivery || false}
                        onChange={(e) => handleConfigChange('channel_specific_settings', {
                          ...localStep.config?.channel_specific_settings,
                          track_delivery: e.target.checked
                        })}
                        disabled={saving}
                      />
                    }
                    label="Track Delivery"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localStep.config?.channel_specific_settings?.track_replies || false}
                        onChange={(e) => handleConfigChange('channel_specific_settings', {
                          ...localStep.config?.channel_specific_settings,
                          track_replies: e.target.checked
                        })}
                        disabled={saving}
                      />
                    }
                    label="Track Replies"
                  />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.primary" gutterBottom>
                      Media URLs
                    </Typography>
                    {(localStep.config?.channel_specific_settings?.media_urls || []).map((url, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          fullWidth
                          value={url}
                          onChange={(e) => {
                            const newUrls = [...(localStep.config?.channel_specific_settings?.media_urls || [])];
                            newUrls[index] = e.target.value;
                            handleConfigChange('channel_specific_settings', {
                              ...localStep.config?.channel_specific_settings,
                              media_urls: newUrls
                            });
                          }}
                          size="small"
                          InputLabelProps={{
                            shrink: !!url
                          }}
                          disabled={saving}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newUrls = [...(localStep.config?.channel_specific_settings?.media_urls || [])];
                            newUrls.splice(index, 1);
                            handleConfigChange('channel_specific_settings', {
                              ...localStep.config?.channel_specific_settings,
                              media_urls: newUrls
                            });
                          }}
                          disabled={saving}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newUrls = [...(localStep.config?.channel_specific_settings?.media_urls || []), ''];
                        handleConfigChange('channel_specific_settings', {
                          ...localStep.config?.channel_specific_settings,
                          media_urls: newUrls
                        });
                      }}
                      disabled={saving}
                    >
                      Add Media URL
                    </Button>
                  </Box>
                </Box>
              )}

              {localStep.step_type === 'voice' && (
                <Box sx={{ space: 2 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      value={localStep.config?.channel_specific_settings?.from_number || ''}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        from_number: e.target.value
                      })}
                      label="From Number"
                      placeholder="+1234567890"
                      InputLabelProps={{
                        shrink: !!localStep.config?.channel_specific_settings?.from_number
                      }}
                      disabled={saving}
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      value={localStep.config?.channel_specific_settings?.language || ''}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        language: e.target.value
                      })}
                      label="Language"
                      placeholder="en-US"
                      InputLabelProps={{
                        shrink: !!localStep.config?.channel_specific_settings?.language
                      }}
                      disabled={saving}
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      type="number"
                      value={localStep.config?.channel_specific_settings?.retry_attempts || ''}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        retry_attempts: parseInt(e.target.value)
                      })}
                      label="Retry Attempts"
                      placeholder="3"
                      InputLabelProps={{
                        shrink: !!localStep.config?.channel_specific_settings?.retry_attempts
                      }}
                      disabled={saving}
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      type="number"
                      value={localStep.config?.channel_specific_settings?.retry_delay || ''}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        retry_delay: parseInt(e.target.value)
                      })}
                      label="Retry Delay (seconds)"
                      placeholder="60"
                      InputLabelProps={{
                        shrink: !!localStep.config?.channel_specific_settings?.retry_delay
                      }}
                      disabled={saving}
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <TextField
                      type="number"
                      value={localStep.config?.channel_specific_settings?.call_timeout || ''}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        call_timeout: parseInt(e.target.value)
                      })}
                      label="Call Timeout (seconds)"
                      placeholder="30"
                      InputLabelProps={{
                        shrink: !!localStep.config?.channel_specific_settings?.call_timeout
                      }}
                      disabled={saving}
                    />
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Machine Detection</InputLabel>
                    <Select
                      value={localStep.config?.channel_specific_settings?.machine_detection || 'true'}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        machine_detection: e.target.value
                      })}
                      label="Machine Detection"
                      disabled={saving}
                    >
                      <MenuItem value="true">Enabled</MenuItem>
                      <MenuItem value="false">Disabled</MenuItem>
                      <MenuItem value="prefer_human">Prefer Human</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              {localStep.step_type === 'chat' && (
                <Box sx={{ space: 2 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Platform</InputLabel>
                    <Select
                      value={localStep.config?.channel_specific_settings?.platform || 'whatsapp'}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        platform: e.target.value
                      })}
                      label="Platform"
                      disabled={saving}
                    >
                      <MenuItem value="whatsapp">WhatsApp</MenuItem>
                      <MenuItem value="messenger">Facebook Messenger</MenuItem>
                      <MenuItem value="telegram">Telegram</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select
                      value={localStep.config?.channel_specific_settings?.priority || 'normal'}
                      onChange={(e) => handleConfigChange('channel_specific_settings', {
                        ...localStep.config?.channel_specific_settings,
                        priority: e.target.value
                      })}
                      label="Priority"
                      disabled={saving}
                    >
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="normal">Normal</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localStep.config?.channel_specific_settings?.track_delivery || false}
                        onChange={(e) => handleConfigChange('channel_specific_settings', {
                          ...localStep.config?.channel_specific_settings,
                          track_delivery: e.target.checked
                        })}
                        disabled={saving}
                      />
                    }
                    label="Track Delivery"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={localStep.config?.channel_specific_settings?.track_read || false}
                        onChange={(e) => handleConfigChange('channel_specific_settings', {
                          ...localStep.config?.channel_specific_settings,
                          track_read: e.target.checked
                        })}
                        disabled={saving}
                      />
                    }
                    label="Track Read"
                  />
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.primary" gutterBottom>
                      Media URLs
                    </Typography>
                    {(localStep.config?.channel_specific_settings?.media_urls || []).map((url, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          fullWidth
                          value={url}
                          onChange={(e) => {
                            const newUrls = [...(localStep.config?.channel_specific_settings?.media_urls || [])];
                            newUrls[index] = e.target.value;
                            handleConfigChange('channel_specific_settings', {
                              ...localStep.config?.channel_specific_settings,
                              media_urls: newUrls
                            });
                          }}
                          size="small"
                          InputLabelProps={{
                            shrink: !!url
                          }}
                          disabled={saving}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newUrls = [...(localStep.config?.channel_specific_settings?.media_urls || [])];
                            newUrls.splice(index, 1);
                            handleConfigChange('channel_specific_settings', {
                              ...localStep.config?.channel_specific_settings,
                              media_urls: newUrls
                            });
                          }}
                          disabled={saving}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newUrls = [...(localStep.config?.channel_specific_settings?.media_urls || []), ''];
                        handleConfigChange('channel_specific_settings', {
                          ...localStep.config?.channel_specific_settings,
                          media_urls: newUrls
                        });
                      }}
                      disabled={saving}
                    >
                      Add Media URL
                    </Button>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.primary" gutterBottom>
                      Quick Replies
                    </Typography>
                    {(localStep.config?.channel_specific_settings?.quick_replies || []).map((reply, index) => (
                      <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                        <TextField
                          label="Text"
                          value={reply.text}
                          onChange={(e) => {
                            const newReplies = [...(localStep.config?.channel_specific_settings?.quick_replies || [])];
                            newReplies[index] = { ...reply, text: e.target.value };
                            handleConfigChange('channel_specific_settings', {
                              ...localStep.config?.channel_specific_settings,
                              quick_replies: newReplies
                            });
                          }}
                          size="small"
                          InputLabelProps={{
                            shrink: !!reply.text
                          }}
                          disabled={saving}
                        />
                        <TextField
                          label="Value"
                          value={reply.value}
                          onChange={(e) => {
                            const newReplies = [...(localStep.config?.channel_specific_settings?.quick_replies || [])];
                            newReplies[index] = { ...reply, value: e.target.value };
                            handleConfigChange('channel_specific_settings', {
                              ...localStep.config?.channel_specific_settings,
                              quick_replies: newReplies
                            });
                          }}
                          size="small"
                          InputLabelProps={{
                            shrink: !!reply.value
                          }}
                          disabled={saving}
                        />
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newReplies = [...(localStep.config?.channel_specific_settings?.quick_replies || [])];
                            newReplies.splice(index, 1);
                            handleConfigChange('channel_specific_settings', {
                              ...localStep.config?.channel_specific_settings,
                              quick_replies: newReplies
                            });
                          }}
                          disabled={saving}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => {
                        const newReplies = [...(localStep.config?.channel_specific_settings?.quick_replies || []), { text: '', value: '' }];
                        handleConfigChange('channel_specific_settings', {
                          ...localStep.config?.channel_specific_settings,
                          quick_replies: newReplies
                        });
                      }}
                      disabled={saving}
                    >
                      Add Quick Reply
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </>
        );

      case 'wait_step':
        return (
          <Box sx={{ space: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.primary" gutterBottom>
                  Duration
                </Typography>
                <TextField
                  fullWidth
                  type="number"
                  value={localStep.config?.duration || 1}
                  onChange={(e) => handleConfigChange('duration', parseInt(e.target.value))}
                  disabled={saving}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.primary" gutterBottom>
                  Unit
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={localStep.config?.unit || 'seconds'}
                    onChange={(e) => handleConfigChange('unit', e.target.value)}
                    disabled={saving}
                  >
                    <MenuItem value="seconds">Seconds</MenuItem>
                    <MenuItem value="minutes">Minutes</MenuItem>
                    <MenuItem value="hours">Hours</MenuItem>
                    <MenuItem value="days">Days</MenuItem>
                    <MenuItem value="weeks">Weeks</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        );

      case 'validation_step':
        return (
          <Box sx={{ space: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Validation Type
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={localStep.config?.validation_type || 'field_check'}
                  onChange={(e) => handleConfigChange('validation_type', e.target.value)}
                  disabled={saving}
                >
                  <MenuItem value="field_check">Field Check</MenuItem>
                  <MenuItem value="custom_validation">Custom Validation</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {localStep.config?.validation_type === 'field_check' && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.primary" gutterBottom>
                    Fields to Validate
                  </Typography>
                  <TextField
                    fullWidth
                    value={localStep.config?.fields?.join(', ') || ''}
                    onChange={(e) => handleConfigChange('fields', e.target.value.split(',').map(f => f.trim()))}
                    label="Fields to Validate"
                    placeholder="field1, field2, field3"
                    InputLabelProps={{
                      shrink: !!localStep.config?.fields?.length
                    }}
                    disabled={saving}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.primary" gutterBottom>
                    Validation Criteria
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={JSON.stringify(localStep.config?.criteria || {}, null, 2)}
                    onChange={(e) => {
                      try {
                        const criteria = JSON.parse(e.target.value);
                        handleConfigChange('criteria', criteria);
                      } catch (err) {
                        // Invalid JSON, ignore
                      }
                    }}
                    placeholder='{"field1": {"type": "required"}, "field2": {"type": "email"}}'
                    disabled={saving}
                  />
                </Box>
              </>
            )}

            {localStep.config?.validation_type === 'custom_validation' && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.primary" gutterBottom>
                    Validation Function
                  </Typography>
                  <TextField
                    fullWidth
                    value={localStep.config?.validation_function || ''}
                    onChange={(e) => handleConfigChange('validation_function', e.target.value)}
                    label="Validation Function"
                    placeholder="function_name"
                    InputLabelProps={{
                      shrink: !!localStep.config?.validation_function
                    }}
                    disabled={saving}
                  />
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.primary" gutterBottom>
                    Parameters
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={JSON.stringify(localStep.config?.parameters || {}, null, 2)}
                    onChange={(e) => {
                      try {
                        const parameters = JSON.parse(e.target.value);
                        handleConfigChange('parameters', parameters);
                      } catch (err) {
                        // Invalid JSON, ignore
                      }
                    }}
                    placeholder='{"param1": "value1", "param2": "value2"}'
                    disabled={saving}
                  />
                </Box>
              </>
            )}
          </Box>
        );

      case 'webhook':
        return (
          <Box sx={{ space: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                URL
              </Typography>
              <TextField
                fullWidth
                value={localStep.config?.url || ''}
                onChange={(e) => handleConfigChange('url', e.target.value)}
                label="URL"
                placeholder="https://api.example.com/webhook"
                InputLabelProps={{
                  shrink: !!localStep.config?.url
                }}
                disabled={saving}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Method
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={localStep.config?.method || 'POST'}
                  onChange={(e) => handleConfigChange('method', e.target.value)}
                  disabled={saving}
                >
                  <MenuItem value="GET">GET</MenuItem>
                  <MenuItem value="POST">POST</MenuItem>
                  <MenuItem value="PUT">PUT</MenuItem>
                  <MenuItem value="DELETE">DELETE</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Headers
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={JSON.stringify(localStep.config?.headers || {}, null, 2)}
                onChange={(e) => {
                  try {
                    const headers = JSON.parse(e.target.value);
                    handleConfigChange('headers', headers);
                  } catch (err) {
                    // Invalid JSON, ignore
                  }
                }}
                placeholder='{"Content-Type": "application/json", "Authorization": "Bearer token"}'
                disabled={saving}
              />
            </Box>
            {(localStep.config?.method === 'POST' || localStep.config?.method === 'PUT') && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.primary" gutterBottom>
                  Request Body
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={JSON.stringify(localStep.config?.body || {}, null, 2)}
                  onChange={(e) => {
                    try {
                      const body = JSON.parse(e.target.value);
                      handleConfigChange('body', body);
                    } catch (err) {
                      // Invalid JSON, ignore
                    }
                  }}
                  placeholder='{"key": "value"}'
                  disabled={saving}
                />
              </Box>
            )}
          </Box>
        );

      case 'goal':
      case 'end':
        return (
          <Typography variant="body2" color="text.primary">
            No additional configuration needed for this step type.
          </Typography>
        );

      default:
        return (
          <Typography variant="body2" color="text.primary">
            No configuration options available for this step type.
          </Typography>
        );
    }
  };

  const outgoingConnections = Array.isArray(connections) ? connections.filter(c => c.from_step === step?.id) : [];
  const availableSteps = Array.isArray(steps) ? steps.filter(s => s.id !== step?.id) : [];

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ mb: 1, mx: 1, mt: 1 }}
        >
          {error}
        </Alert>
      )}

      <Box sx={{ 
        p: 1.5, 
        borderBottom: 1, 
        borderColor: 'divider', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        bgcolor: 'background.paper',
      }}>
        <Typography variant="subtitle1" color="text.primary" noWrap>
          {localStep.name}
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          minHeight: '48px',
          '& .MuiTab-root': {
            minHeight: '48px',
            fontSize: '0.875rem',
            color: 'text.primary',
          }
        }}
      >
        <Tab value="settings" label="Settings" />
        <Tab value="connections" label="Connections" />
      </Tabs>

      <Box sx={{ 
        flex: 1, 
        overflow: 'auto', 
        p: 1.5,
        '& > *': {
          mb: 1.5,
        }
      }}>
        {activeTab === 'settings' ? (
          <Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Step Name
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={localStep.name}
                onChange={handleNameChange}
                disabled={saving}
              />
            </Box>

            {renderConfigFields()}
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" color="text.primary">
                Connections
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => setIsConnectionModalOpen(true)}
                disabled={saving}
              >
                Add Connection
              </Button>
            </Box>

            {outgoingConnections.length > 0 ? (
              outgoingConnections.map((connection) => {
                const targetStep = steps.find(s => s.id === connection.to_step);
                const isDeleting = connection.isDeleting;
                
                return (
                  <Card key={connection.id} sx={{ mb: 1 }}>
                    <CardContent sx={{ p: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ArrowForwardIcon fontSize="small" color="action" />
                          <Box>
                            <Typography variant="body2" color="text.primary" noWrap>
                              {targetStep?.name}
                            </Typography>
                            <Badge
                              variant="outline"
                              sx={{ mt: 0.5 }}
                            >
                              {connection.condition_label || 'Immediate'}
                            </Badge>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="Edit Connection">
                            <IconButton
                              size="small"
                              onClick={() => handleEditConnection(connection)}
                              sx={{ p: 0.5 }}
                              disabled={isDeleting}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={isDeleting ? "Deleting..." : "Delete Connection"}>
                            <IconButton
                              size="small"
                              onClick={() => onDeleteConnection(connection.id)}
                              sx={{ 
                                p: 0.5,
                                color: isDeleting ? 'error.main' : 'inherit',
                              }}
                              disabled={isDeleting}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })
            ) : (
              <Box sx={{ p: 1.5, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" color="text.primary">
                  No connections yet. Click "Add Connection" to create one.
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      <Divider />
      <Box sx={{ p: 1.5, display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="small"
          disabled={!hasChanges || saving}
          onClick={handleUpdateStep}
        >
          {saving ? <CircularProgress size={24} /> : 'Update Step'}
        </Button>
        <Button
          variant="contained"
          color="error"
          fullWidth
          size="small"
          disabled={saving}
          onClick={handleDeleteStep}
        >
          Delete Step
        </Button>
      </Box>

      <ConnectionModal
        open={isConnectionModalOpen}
        onClose={() => {
          setIsConnectionModalOpen(false);
          setEditingConnection(null);
        }}
        editingConnection={editingConnection}
        selectedTargetStep={selectedTargetStep}
        setSelectedTargetStep={setSelectedTargetStep}
        triggerType={triggerType}
        setTriggerType={setTriggerType}
        delayDuration={delayDuration}
        setDelayDuration={setDelayDuration}
        delayUnit={delayUnit}
        setDelayUnit={setDelayUnit}
        funnelStep={funnelStep}
        setFunnelStep={setFunnelStep}
        eventType={eventType}
        setEventType={setEventType}
        conditionLabel={conditionLabel}
        setConditionLabel={setConditionLabel}
        conditionConfig={conditionConfig}
        setConditionConfig={setConditionConfig}
        priority={priority}
        setPriority={setPriority}
        availableSteps={availableSteps}
        onCreateConnection={handleCreateConnection}
        isValidConnection={isValidConnection}
        journey={journey}
        step={localStep}
      />
    </Box>
  );
};

export default StepSidebar; 